/**
 * @file Universities/index.js
 * @description Main component for managing universities in the admin panel.
 *              Provides listing, pagination, search, and CRUD operations for university data.
 * @created June 2025
 */

import React, { Component } from "react";
import { Spin, Row, Col, Button, Table, Pagination, Input } from "antd";
import { getUniversitiesList } from "../../services";
import AddEditUniversityModal from "./AddEditUniversityModal";
import { showSuccessNotification } from "../../helpers";
import debounce from 'lodash/debounce';

/**
 * @class Universities
 * @extends Component
 * @description Component for displaying and managing the list of universities
 */
class Universities extends Component {
  /**
   * @constructor
   * @param {Object} props - Component props
   */
  constructor(props) {
    super(props);
    this.state = {
      universitiesList: [], // Array to hold the list of universities
      pageNo: 1, // Current page number for pagination
      perPage: 10, // Number of records to show per page
      totalRecords: 0, // Total number of records for pagination
      isProcessing: false, // Loading state indicator
      showModal: false, // Controls visibility of add/edit modal
      selectedUniversity: null, // Holds the university object being edited
      searchQuery: '' // Holds the current search query
    };

    // Create a debounced version of fetchUniversities
    this.debouncedFetch = debounce(this.fetchUniversities, 500);
  }

  /**
   * @lifecycle
   * @description Fetch universities when component mounts
   */
  componentDidMount() {
    this.fetchUniversities();
  }

  /**
   * @function handleSearch
   * @description Handles search input changes
   * @param {Event} e - The input change event
   * @returns {void}
   */
  handleSearch = e => {
    const searchQuery = e.target.value;
    this.setState({ 
      searchQuery,
      pageNo: 1 // Reset to first page when searching
    }, () => {
      this.debouncedFetch();
    });
  };

  /**
   * @function fetchUniversities
   * @description Fetches the list of universities from the API based on pagination and search settings
   * @returns {void}
   */
  fetchUniversities = () => {
    this.setState({ isProcessing: true });
    const { perPage, pageNo, searchQuery } = this.state;
    
    getUniversitiesList(pageNo, perPage, searchQuery)
      .then(response => {
        // Extract data and metadata from the response
        const { data, meta } = response.data;
        this.setState({
          universitiesList: data,
          totalRecords: meta?.total_records || 0
        });
      })
      .catch(error => {
        console.error("Error fetching universities:", error);
        showSuccessNotification("Failed to load universities", "error");
        // Reset data on error
        this.setState({ universitiesList: [], totalRecords: 0 });
      })
      .finally(() => {
        // Always turn off loading indicator
        this.setState({ isProcessing: false });
      });
  };

  /**
   * @function onPageChange
   * @description Handles pagination page change event
   * @param {number} pageNo - The new page number
   * @returns {void}
   */
  onPageChange = pageNo => {
    this.setState({ pageNo }, () => {
      this.fetchUniversities();
    });
  };

  /**
   * @function toggleModal
   * @description Toggles the visibility of the add/edit university modal
   * @returns {void}
   */
  toggleModal = () => {
    // Reset selected university when closing the modal
    if (this.state.showModal) {
      this.setState({ selectedUniversity: null });
    }
    this.setState({ showModal: !this.state.showModal });
  };


  /**
   * @function handleEditUniversity
   * @description Prepares university data for editing and opens the edit modal
   * @param {Object} record - The university record to be edited
   * @returns {void}
   */
  handleEditUniversity = record => {
    // Extract relevant attributes from the record
    const {
      name,
      rank
    } = record.attributes;

    // Create a simplified object for the edit form
    const selectedUniversity = {
      id: record.id,
      name,
      rank
    };
    
    // Save the selected university to state and open the modal
    this.setState({ selectedUniversity }, () => {
      this.toggleModal();
    });
  };


  /**
   * @function render
   * @description Renders the Universities component
   * @returns {JSX.Element} Rendered component
   */
  render() {
    // Destructure values from state for cleaner code
    const {
      universitiesList,
      totalRecords,
      pageNo,
      perPage,
      isProcessing,
      showModal,
      selectedUniversity,
      searchQuery
    } = this.state;
    
    // Define table columns configuration
    const columns = [
      {
        title: "Name",
        align: "left",
        dataIndex: "attributes.name"
      },
      {
        title: "Rank",
        dataIndex: "attributes.rank"
      },
      {
        title: "Actions",
        key: "actions",
        render: record => (
          <div>
            {/* Edit button for modifying university details */}
            {
              <Button
                type="default"
                shape="circle"
                icon="edit"
                className="mr-10"
                onClick={() => this.handleEditUniversity(record)}
              />
            }
          </div>
        )
      }
    ];

    return (
      <Spin spinning={isProcessing}>
        <div className="content-container">
          <div className="backgroundWhite">
            {/* Header with title and add button */}
            <Row type="flex" justify="space-between" className="mb-10">
              <Col className="heading-domain">Basic Universities</Col>
              <Col>
                <Button type="primary" onClick={this.toggleModal}>
                  Add New University
                </Button>
              </Col>
            </Row>

            {/* Search input */}
            <Row className="mb-10">
              <Col span={8}>
                <Input.Search
                  placeholder="Enter university name to search"
                  value={searchQuery}
                  onChange={this.handleSearch}
                  allowClear
                />
              </Col>
            </Row>

            {/* Table to display university data */}
            <Table
              pagination={false} // We're using custom pagination below
              dataSource={universitiesList}
              columns={columns}
              rowKey="id" // Use id as the unique key for each row
              loading={isProcessing} // Show loading spinner when fetching data
              locale={{ emptyText: searchQuery ? 'No universities found matching your search' : 'No universities found' }} // Custom message for empty data
            />
            {/* Custom pagination component */}
            <Pagination
              className="mt-10"
              total={totalRecords} // Total number of records from API
              current={pageNo} // Current page number
              onChange={this.onPageChange} // Handle page changes
              pageSize={perPage} // Number of items per page
              defaultCurrent={pageNo} // Default page to show
              showSizeChanger // Allow changing items per page
              onShowSizeChange={(current, size) => {
                // Reset to first page when changing page size
                this.setState({ pageNo: 1, perPage: size }, () => {
                  this.fetchUniversities(); // Reload data with new page size
                });
              }}
              pageSizeOptions={['10', '20', '50', '100']} // Available page size options
              showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} universities`} // Descriptive text
            />
          </div>
        </div>
        {/* Render add/edit modal when needed */}
        {showModal && (
          <AddEditUniversityModal
            showModal={showModal} // Control visibility
            toggleModal={this.toggleModal} // Function to close modal
            selectedUniversity={selectedUniversity} // University data for editing (null for add)
            callback={this.fetchUniversities} // Refresh data after successful operation
          />
        )}
      </Spin>
    );
  }
}

export default Universities;
